import { useState, useRef, useEffect, useCallback } from "react"
import { Download, Save, Scissors, Trash2, Play, Pause, Volume2, VolumeX, Maximize, RotateCcw } from "lucide-react"
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { FFmpeg } from "@ffmpeg/ffmpeg"
import { fetchFile, toBlobURL } from "@ffmpeg/util"
import { cn } from "@/lib/utils"

interface RecordingPreviewModalProps {
    isOpen: boolean
    onClose: () => void
    blob: Blob | null
    onDownload: () => void
    onSaveTutorial: (language: 'bn' | 'en') => void
    isSaving?: boolean
}

interface TimelineSegment {
    id: string
    start: number
    end: number
}

function formatTime(seconds: number) {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return "0:00"
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
}

export function RecordingPreviewModal({
    isOpen, onClose, blob, onDownload, onSaveTutorial, isSaving = false
}: RecordingPreviewModalProps) {
    const [language, setLanguage] = useState<'bn' | 'en'>('en')

    // Video State
    const [videoUrl, setVideoUrl] = useState<string | null>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const playerRef = useRef<HTMLDivElement>(null)

    // Segment State
    const [duration, setDuration] = useState(0)
    const [isDurationReady, setIsDurationReady] = useState(false)
    const [segments, setSegments] = useState<TimelineSegment[]>([])
    const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null)

    // Custom Player State
    const [currentTime, setCurrentTime] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [volume, setVolume] = useState(1)
    const [isMuted, setIsMuted] = useState(false)
    const [showControls, setShowControls] = useState(true)
    const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Editor State
    const [isProcessing, setIsProcessing] = useState(false)
    const [ffmpegLoaded, setFfmpegLoaded] = useState(false)
    const ffmpegRef = useRef<FFmpeg | null>(null)

    // ── FFmpeg Loader ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || ffmpegLoaded) return
        const load = async () => {
            const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'
            const ffmpeg = new FFmpeg()
            ffmpegRef.current = ffmpeg
            await ffmpeg.load({
                coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
                wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
            })
            setFfmpegLoaded(true)
        }
        load().catch(err => console.error("FFmpeg load failed:", err))
    }, [isOpen, ffmpegLoaded])

    // ── Blob → URL + WebM Duration Fix ───────────────────────────────────────
    useEffect(() => {
        if (!blob) {
            setVideoUrl(null); setDuration(0); setIsDurationReady(false); setSegments([]); return
        }
        const url = URL.createObjectURL(blob)
        setVideoUrl(url)
        setDuration(0); setIsDurationReady(false); setSegments([]); setCurrentTime(0)

        const probe = document.createElement('video')
        probe.preload = 'metadata'
        probe.src = url

        const applyDuration = (dur: number) => {
            if (!isFinite(dur) || dur <= 0) return
            setDuration(dur); setIsDurationReady(true)
            setSegments([{ id: crypto.randomUUID(), start: 0, end: dur }])
            setActiveSegmentId(null)
        }

        probe.onloadedmetadata = () => {
            if (isFinite(probe.duration) && probe.duration > 0) applyDuration(probe.duration)
            else probe.currentTime = 1e10 // seek-to-end WebM fix
        }
        probe.onseeked = () => {
            if (isFinite(probe.duration) && probe.duration > 0) {
                applyDuration(probe.duration)
                probe.currentTime = 0
            }
        }
        probe.onerror = () => console.error("Duration probe failed")

        return () => {
            probe.onloadedmetadata = null; probe.onseeked = null; probe.onerror = null
            probe.src = ''
            URL.revokeObjectURL(url)
            setVideoUrl(null); setIsDurationReady(false)
        }
    }, [blob])

    // ── Player Controls ───────────────────────────────────────────────────────
    const togglePlay = useCallback(() => {
        const vid = videoRef.current
        if (!vid) return
        if (vid.paused) vid.play()
        else vid.pause()
    }, [])

    const handleSeek = useCallback((value: number[]) => {
        const vid = videoRef.current
        if (!vid) return
        vid.currentTime = value[0]
        setCurrentTime(value[0])
    }, [])

    const handleVolumeChange = useCallback((value: number[]) => {
        const vid = videoRef.current
        if (!vid) return
        const v = value[0]
        vid.volume = v
        setVolume(v)
        setIsMuted(v === 0)
    }, [])

    const toggleMute = useCallback(() => {
        const vid = videoRef.current
        if (!vid) return
        const next = !vid.muted
        vid.muted = next
        setIsMuted(next)
    }, [])

    const handleFullscreen = useCallback(() => {
        const el = playerRef.current
        if (!el) return
        if (!document.fullscreenElement) el.requestFullscreen?.()
        else document.exitFullscreen?.()
    }, [])

    const handleRestart = useCallback(() => {
        const vid = videoRef.current
        if (!vid) return
        vid.currentTime = 0
        vid.play()
    }, [])

    // Auto-hide controls on inactivity during playback
    const resetControlsTimer = useCallback(() => {
        setShowControls(true)
        if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current)
        if (isPlaying) {
            hideControlsTimerRef.current = setTimeout(() => setShowControls(false), 3000)
        }
    }, [isPlaying])

    useEffect(() => {
        if (!isPlaying) setShowControls(true)
        return () => { if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current) }
    }, [isPlaying])

    // ── Segment Logic ─────────────────────────────────────────────────────────
    const handleTimeUpdate = useCallback(() => {
        const vid = videoRef.current
        if (!vid) return
        const t = vid.currentTime
        setCurrentTime(t)
        if (segments.length === 0) return
        const cur = segments.find(s => t >= s.start && t < s.end)
        if (cur) { setActiveSegmentId(cur.id) }
        else {
            const next = segments.find(s => s.start > t)
            if (next) vid.currentTime = next.start
            else { vid.pause(); if (segments[0]) vid.currentTime = segments[0].start }
        }
    }, [segments])

    const handleSplit = useCallback(() => {
        const vid = videoRef.current
        if (!vid || !isDurationReady) return
        const t = vid.currentTime
        const idx = segments.findIndex(s => t > s.start && t < s.end)
        if (idx === -1) return
        const seg = segments[idx]
        if (t - seg.start < 0.5 || seg.end - t < 0.5) return
        const updated = [...segments]
        updated.splice(idx, 1,
            { ...seg, end: t },
            { id: crypto.randomUUID(), start: t, end: seg.end }
        )
        setSegments(updated)
    }, [segments, isDurationReady])

    const handleDeleteSegment = useCallback((id: string) => {
        if (segments.length <= 1) { alert("Cannot delete the last segment."); return }
        setSegments(prev => prev.filter(s => s.id !== id))
    }, [segments.length])

    // ── FFmpeg Processing ─────────────────────────────────────────────────────
    const performMerge = useCallback(async (): Promise<Blob | null> => {
        if (!ffmpegRef.current || !blob) return null
        if (segments.length === 1 && segments[0].start === 0 && segments[0].end === duration) return blob
        setIsProcessing(true)
        const ff = ffmpegRef.current
        try {
            await ff.writeFile('input.webm', await fetchFile(blob))
            const fileList: string[] = []
            for (let i = 0; i < segments.length; i++) {
                const s = segments[i]
                const part = `part_${i}.webm`
                await ff.exec(['-i', 'input.webm', '-ss', `${s.start}`, '-to', `${s.end}`, '-c', 'copy', part])
                fileList.push(`file '${part}'`)
            }
            await ff.writeFile('list.txt', fileList.join('\n'))
            await ff.exec(['-f', 'concat', '-safe', '0', '-i', 'list.txt', '-c', 'copy', 'merged.webm'])
            const data = await ff.readFile('merged.webm')
            return new Blob([data as any], { type: 'video/webm' })
        } catch (err) {
            console.error("Merge failed:", err)
            alert("Merge failed. Using original.")
            return blob
        } finally { setIsProcessing(false) }
    }, [blob, segments, duration])

    const handleDownloadClick = useCallback(async () => {
        const merged = await performMerge()
        const result = merged || blob
        if (!result) return
        const url = URL.createObjectURL(result)
        Object.assign(document.createElement('a'), {
            href: url, download: `recording-${new Date().toISOString()}.webm`
        }).click()
        URL.revokeObjectURL(url)
    }, [performMerge, blob])

    const handleSaveClick = useCallback(async () => {
        const merged = await performMerge()
        const result = merged || blob
        if (result) (onSaveTutorial as any)(language, result)
    }, [performMerge, blob, onSaveTutorial, language])

    if (!blob) return null

    const safeDuration = isFinite(duration) && duration > 0 ? duration : 1
    const progress = (currentTime / safeDuration) * 100

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onClose()}>
            <DialogContent className="sm:max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
                <DialogHeader className="p-4 pb-2 shrink-0 border-b">
                    <DialogTitle>Recording Editor</DialogTitle>
                    <DialogDescription>Split and delete segments. Gaps will be skipped during playback.</DialogDescription>
                </DialogHeader>

                <div className="flex-1 flex flex-col min-h-0 bg-zinc-950">
                    {/* ── Custom Video Player ── */}
                    <div
                        ref={playerRef}
                        className="flex-1 relative min-h-0 flex items-center justify-center group/player"
                        onMouseMove={resetControlsTimer}
                        onClick={togglePlay}
                    >
                        {videoUrl ? (
                            <video
                                ref={videoRef}
                                src={videoUrl}
                                className="w-full h-full object-contain max-h-full"
                                onTimeUpdate={handleTimeUpdate}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onDurationChange={() => {
                                    const vid = videoRef.current
                                    if (vid && isFinite(vid.duration) && vid.duration > 0 && vid.duration !== duration) {
                                        setDuration(vid.duration); setIsDurationReady(true)
                                        if (segments.length === 0)
                                            setSegments([{ id: crypto.randomUUID(), start: 0, end: vid.duration }])
                                    }
                                }}
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-3 text-zinc-500">
                                <div className="w-8 h-8 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
                                <span className="text-sm">Loading video…</span>
                            </div>
                        )}

                        {/* Processing Overlay */}
                        {isProcessing && (
                            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white backdrop-blur-sm z-50 gap-3">
                                <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span className="font-medium">Processing…</span>
                            </div>
                        )}

                        {/* Big play icon hint when paused */}
                        {!isPlaying && videoUrl && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                                    <Play className="w-7 h-7 text-white translate-x-0.5" />
                                </div>
                            </div>
                        )}

                        {/* Controls Bar — fades out on inactivity */}
                        <div
                            className={cn(
                                "absolute bottom-0 left-0 right-0 px-3 pb-3 pt-8 transition-opacity duration-300",
                                "bg-gradient-to-t from-black/80 via-black/40 to-transparent",
                                showControls || !isPlaying ? "opacity-100" : "opacity-0 pointer-events-none"
                            )}
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Seek Bar */}
                            <div className="mb-2">
                                <Slider
                                    value={[currentTime]}
                                    min={0}
                                    max={safeDuration}
                                    step={0.1}
                                    onValueChange={handleSeek}
                                    disabled={!isDurationReady}
                                    className="[&>span:first-child]:h-1 [&>span:first-child]:bg-white/20 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:border-0 [&_[role=slider]]:shadow-none [&>span:first-child>span]:bg-red-500"
                                />
                            </div>

                            {/* Controls Row */}
                            <div className="flex items-center gap-3">
                                {/* Play / Pause */}
                                <button
                                    onClick={togglePlay}
                                    className="text-white hover:text-red-400 transition-colors"
                                    aria-label={isPlaying ? "Pause" : "Play"}
                                >
                                    {isPlaying
                                        ? <Pause className="w-5 h-5 fill-current" />
                                        : <Play className="w-5 h-5 fill-current" />}
                                </button>

                                {/* Restart */}
                                <button
                                    onClick={handleRestart}
                                    className="text-white/70 hover:text-white transition-colors"
                                    aria-label="Restart"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>

                                {/* Volume */}
                                <button onClick={toggleMute} className="text-white/70 hover:text-white transition-colors">
                                    {isMuted || volume === 0
                                        ? <VolumeX className="w-4 h-4" />
                                        : <Volume2 className="w-4 h-4" />}
                                </button>
                                <div className="w-20">
                                    <Slider
                                        value={[isMuted ? 0 : volume]}
                                        min={0} max={1} step={0.05}
                                        onValueChange={handleVolumeChange}
                                        className="[&>span:first-child]:h-1 [&>span:first-child]:bg-white/20 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:border-0 [&_[role=slider]]:shadow-none [&>span:first-child>span]:bg-white"
                                    />
                                </div>

                                {/* Time */}
                                <span className="text-white/80 text-xs tabular-nums ml-1">
                                    {formatTime(currentTime)} / {isDurationReady ? formatTime(duration) : '…'}
                                </span>

                                <div className="flex-1" />

                                {/* Fullscreen */}
                                <button
                                    onClick={handleFullscreen}
                                    className="text-white/70 hover:text-white transition-colors"
                                    aria-label="Fullscreen"
                                >
                                    <Maximize className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Timeline / Editor ── */}
                    <div className="shrink-0 border-t border-zinc-800 bg-zinc-900 p-4 space-y-3">
                        <div className="max-w-4xl mx-auto w-full space-y-3">
                            {/* Header row */}
                            <div className="flex items-center justify-between text-xs text-zinc-500 uppercase tracking-wider font-semibold">
                                <span className="flex items-center gap-2">
                                    Timeline
                                    {!isDurationReady && (
                                        <span className="normal-case font-normal text-amber-400 flex items-center gap-1">
                                            <span className="w-3 h-3 border border-amber-400 border-t-transparent rounded-full animate-spin inline-block" />
                                            Detecting duration…
                                        </span>
                                    )}
                                </span>
                                <span className="text-zinc-400">{segments.length} Segment{segments.length !== 1 ? 's' : ''}</span>
                            </div>

                            {/* Visual Timeline */}
                            <div className="relative h-12 bg-zinc-800 rounded-lg overflow-hidden flex w-full select-none">
                                {isDurationReady ? segments.map(s => {
                                    const left = (s.start / safeDuration) * 100
                                    const width = ((s.end - s.start) / safeDuration) * 100
                                    const isActive = activeSegmentId === s.id
                                    return (
                                        <div
                                            key={s.id}
                                            className={cn(
                                                "absolute h-full top-0 border-r border-zinc-900 flex items-center justify-center group/seg cursor-pointer transition-colors",
                                                isActive ? "bg-red-500/80" : "bg-red-500/30 hover:bg-red-500/50"
                                            )}
                                            style={{ left: `${left}%`, width: `${Math.max(width, 0.5)}%` }}
                                            onClick={() => { if (videoRef.current) videoRef.current.currentTime = s.start }}
                                            title={`${formatTime(s.start)} – ${formatTime(s.end)}`}
                                        >
                                            <button
                                                onClick={e => { e.stopPropagation(); handleDeleteSegment(s.id) }}
                                                className="opacity-0 group-hover/seg:opacity-100 bg-red-700 text-white p-0.5 rounded-full scale-75 hover:scale-100 transition-all"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )
                                }) : (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-zinc-600 border-t-zinc-400 rounded-full animate-spin" />
                                    </div>
                                )}

                                {/* Playhead */}
                                {isDurationReady && (
                                    <div
                                        className="absolute top-0 bottom-0 w-0.5 bg-white z-10 pointer-events-none"
                                        style={{ left: `${(currentTime / safeDuration) * 100}%` }}
                                    />
                                )}
                            </div>

                            {/* Toolbar */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSplit}
                                        disabled={!isDurationReady || !activeSegmentId}
                                        className="gap-2 h-8 text-xs bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white"
                                    >
                                        <Scissors className="w-3.5 h-3.5 -rotate-90" />
                                        Split at Playhead
                                    </Button>
                                    {!ffmpegLoaded && (
                                        <span className="text-xs text-amber-400 flex items-center gap-1.5">
                                            <span className="w-3 h-3 border border-amber-400 border-t-transparent rounded-full animate-spin inline-block" />
                                            Loading editor…
                                        </span>
                                    )}
                                </div>

                                {/* Language */}
                                <div className="flex items-center gap-3 bg-zinc-800 px-3 py-1.5 rounded-md border border-zinc-700">
                                    <span className="text-xs font-semibold text-zinc-400">Language:</span>
                                    {(['en', 'bn'] as const).map(lang => (
                                        <label key={lang} className="flex items-center gap-1.5 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="language"
                                                value={lang}
                                                checked={language === lang}
                                                onChange={() => setLanguage(lang)}
                                                className="accent-red-500"
                                            />
                                            <span className="text-xs text-zinc-300">{lang === 'en' ? 'English' : 'Bengali'}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="shrink-0 flex-col sm:flex-row gap-2 p-4 border-t bg-zinc-900">
                    <Button variant="outline" onClick={onClose} disabled={isProcessing}
                        className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white">
                        Discard
                    </Button>
                    <Button variant="secondary" onClick={handleDownloadClick}
                        disabled={isProcessing || !ffmpegLoaded || !isDurationReady}
                        className="bg-zinc-700 hover:bg-zinc-600 text-white border-zinc-600">
                        <Download className="w-4 h-4 mr-2" />
                        Download
                    </Button>
                    <Button onClick={handleSaveClick}
                        disabled={isSaving || isProcessing || !ffmpegLoaded || !isDurationReady}
                        className="bg-red-600 hover:bg-red-700 text-white">
                        {isSaving || isProcessing ? (
                            <>
                                <span className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                                {isProcessing ? "Processing…" : "Saving…"}
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                Save as Tutorial
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
